import React from "react";
import styled from "styled-components/native";
import { VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Spacer } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import LineViewContainer from "./utils/LineView";
import { renderDateTime, renderRequestStatus } from "./utils/RequestsScreen";
import { getRequestType, RequestTypes } from "./utils/utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { getBoxShadow } from "@utils/product/productValidators";
import { DefaultTheme } from "styled-components/native";
import { getPaymentType } from "@components/molecules/BMH/BmhForm";
import { StockItemRequestClass } from "@src/types/HistoryRequest";

interface RequestStatusCardProps {
  stockItemRequest: StockItemRequestClass;
  isCustomApp: boolean;
  requestName?: string;
}

const RequestStatusCard = ({
  stockItemRequest,
  isCustomApp,
  requestName,
}: RequestStatusCardProps) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const { t } = useTranslation();
  const userInfo = useUserInfo();

  return (
    <CardContainer>
      <StatusContainer>
        <StatusTopContainer>
          <VDBText type={typography["H3-R"]} color={theme["core-05"]}>
            {t("requestHistoryDetail.status.text", "Status")}
          </VDBText>
          <VDBText
            type={typography["H3-M"]}
            color={
              requestName === RequestTypes.Order &&
              stockItemRequest?.track_status_color
                ? stockItemRequest?.track_status_color
                : (theme[
                    renderRequestStatus(
                      stockItemRequest?.memo_status
                        ? stockItemRequest?.request_notification_msg.toLowerCase()
                        : stockItemRequest?.status,
                      t
                    ).colorCode as keyof DefaultTheme
                  ] as string)
            }
          >
            {requestName === RequestTypes.Order &&
            stockItemRequest?.track_status
              ? stockItemRequest?.track_status
              : renderRequestStatus(
                  stockItemRequest?.memo_status
                    ? stockItemRequest?.request_notification_msg.toLowerCase()
                    : stockItemRequest?.status,
                  t
                ).status}
          </VDBText>
        </StatusTopContainer>
        <Spacer size={SpaceType.ExtraSmall} />
        <StatusRightView>
          <VDBText
            type={
              theme.isLargeScreen ? typography["Body-R"] : typography["Minor-R"]
            }
            color={theme["core-06"]}
          >
            {renderDateTime(
              requestName === RequestTypes.Order
                ? stockItemRequest?.created_at
                : stockItemRequest?.updated_at,
              "DD MMM, y [at] hh:mm A"
            )}
          </VDBText>
        </StatusRightView>
      </StatusContainer>
      <LineViewContainer background={"core-03"} />
      <StatusBottomContainer>
        <LabelViewContainer>
          <LeftText>
            <VDBText
              type={
                theme.isLargeScreen
                  ? typography["Body-R"]
                  : typography["Minor-R"]
              }
              color={theme["core-06"]}
            >
              {getRequestType(stockItemRequest.request_type) ===
              RequestTypes.Quote
                ? t("requestHistoryDetail.quoteId.text")
                : requestName === RequestTypes.Order
                ? t("requestHistoryDetail.OrderId.text")
                : t("requestHistoryDetail.requestId.text")}
            </VDBText>
          </LeftText>
          <RightText>
            <VDBText
              type={
                theme.isLargeScreen
                  ? typography["Body-M"]
                  : typography["Minor-M"]
              }
              color={theme["core-05"]}
            >
              {stockItemRequest?.[
                requestName === RequestTypes.Order ? "order_id" : "id"
              ]?.toString()}
            </VDBText>
          </RightText>
        </LabelViewContainer>

        <LabelViewContainer>
          <LeftText>
            <VDBText
              type={
                theme.isLargeScreen
                  ? typography["Body-R"]
                  : typography["Minor-R"]
              }
              color={theme["core-06"]}
            >
              {getRequestType(stockItemRequest.request_type) ===
              RequestTypes.Quote
                ? t("requestHistoryDetail.generatedOn.text")
                : requestName === RequestTypes.Order
                ? t("requestHistoryDetail.orderOn.text")
                : t("requestHistoryDetail.requestOn.text")}
            </VDBText>
          </LeftText>
          <RightText>
            <VDBText
              type={
                theme.isLargeScreen
                  ? typography["Body-M"]
                  : typography["Minor-M"]
              }
              color={theme["core-05"]}
            >
              {renderDateTime(
                stockItemRequest?.created_at,
                "DD MMM, y [at] hh:mm A"
              )}
            </VDBText>
          </RightText>
        </LabelViewContainer>
        {!isCustomApp &&
          userInfo.isPAUserOrPASalesPerson() &&
          stockItemRequest?.view_type === "seller_view" && (
            <LabelViewContainer>
              <LeftText>
                <VDBText
                  type={
                    theme.isLargeScreen
                      ? typography["Body-R"]
                      : typography["Minor-R"]
                  }
                  color={theme["core-06"]}
                >
                  {t("requestHistoryDetail.requestBy.text")}
                </VDBText>
              </LeftText>
              <RightText>
                <VDBText
                  type={
                    theme.isLargeScreen
                      ? typography["Body-M"]
                      : typography["Minor-M"]
                  }
                  color={theme["core-05"]}
                >
                  {stockItemRequest?.buyer_location?.contact_person?.toString()}
                </VDBText>
              </RightText>
            </LabelViewContainer>
          )}

        {getRequestType(
          requestName === RequestTypes.Order
            ? requestName
            : stockItemRequest?.request_type || ""
        ) === RequestTypes.Buy && (
          <LabelViewContainer>
            <LeftText>
              <VDBText
                type={
                  theme.isLargeScreen
                    ? typography["Body-R"]
                    : typography["Minor-R"]
                }
                color={theme["core-06"]}
              >
                {t("requestHistoryDetail.type.text")}
              </VDBText>
            </LeftText>
            <RightText>
              <VDBText
                type={
                  theme.isLargeScreen
                    ? typography["Body-M"]
                    : typography["Minor-M"]
                }
                color={theme["core-05"]}
              >
                {stockItemRequest?.translated_payment_type ??
                  getPaymentType(stockItemRequest?.payment_type, t)}
              </VDBText>
            </RightText>
          </LabelViewContainer>
        )}

        {getRequestType(stockItemRequest.request_type || "") ===
          RequestTypes.Buy &&
          stockItemRequest.payment_type === "Invoice" &&
          stockItemRequest?.pay_within && (
            <LabelViewContainer>
              <LeftText>
                <VDBText
                  type={
                    theme.isLargeScreen
                      ? typography["Body-R"]
                      : typography["Minor-R"]
                  }
                  color={theme["core-06"]}
                >
                  {t("requestHistoryDetail.payWithIn.text")}
                </VDBText>
              </LeftText>
              <RightText>
                <VDBText
                  type={
                    theme.isLargeScreen
                      ? typography["Body-M"]
                      : typography["Minor-M"]
                  }
                  color={theme["core-05"]}
                >
                  {stockItemRequest?.pay_within +
                    " " +
                    t("requestHistoryDetail.days.text")}
                </VDBText>
              </RightText>
            </LabelViewContainer>
          )}

        {getRequestType(stockItemRequest.request_type || "") ===
          RequestTypes.Hold &&
          stockItemRequest.request_notification_msg && (
            <LabelViewContainer>
              <LeftText>
                <VDBText
                  type={
                    theme.isLargeScreen
                      ? typography["Body-R"]
                      : typography["Minor-R"]
                  }
                  color={theme["core-06"]}
                >
                  {t("requestHistoryDetail.timeToHold.text")}
                </VDBText>
              </LeftText>
              <RightText>
                <VDBText
                  type={
                    theme.isLargeScreen
                      ? typography["Body-M"]
                      : typography["Minor-M"]
                  }
                  color={theme["core-05"]}
                >
                  {["expired", "withdraw", "decline"]?.includes(
                    stockItemRequest?.status
                  )
                    ? "0 Hrs"
                    : stockItemRequest.request_notification_msg}
                </VDBText>
              </RightText>
            </LabelViewContainer>
          )}
      </StatusBottomContainer>
    </CardContainer>
  );
};

const CardContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme["core-03"]};
  box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Gentle)};
`;

const StatusContainer = styled.View`
  padding: ${SpaceType.Extra}px ${SpaceType.Double}px;
`;

const StatusTopContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: top;
`;

const StatusRightView = styled.View`
  align-self: flex-end;
`;

const StatusBottomContainer = styled.View`
  padding-top: ${SpaceType.ExtraSmall}px;
  padding-bottom: ${SpaceType.Basic}px;
`;

const LabelViewContainer = styled.View`
  padding: ${SpaceType.ExtraSmall}px ${SpaceType.Double}px;
  flex-direction: row;
`;

const LeftText = styled.View`
  width: ${({ theme }) => (theme.isMobile ? 35 : 18)}%;
`;

const RightText = styled.View`
  max-width: 800px;
`;

export default RequestStatusCard;
